import React from "react";
import { Check, X, TriangleAlert, Lock, Star } from "lucide-react";
import {
  hexagonRoot,
  successIconClass,
  failIconClass,
  warningIconClass,
  informationIconClass,
  highlightIconClass,
  lockedIconClass,
  whiteIconClass,
  darkIconClass,
  skippedIconClass,
  hexagonShape,
  iconContainer,
  iconClass,
  charLabelClass,
} from "./hexagon.css";

export type HexagonVariant =
  | "success"
  | "fail"
  | "warning"
  | "information"
  | "highlight"
  | "locked"
  | "white"
  | "dark"
  | "skipped";

export interface HexagonProps {
  type: HexagonVariant;
  size?: number;
  character?: string;
}

const InformationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="3"
    height="17"
    viewBox="0 0 3 17"
    fill="none"
  >
    <path
      d="M1.25 15.25V6.25M1.25 1.25H1.2575"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const VARIANT_CONFIG: Record<
  HexagonVariant,
  {
    IconComponent?: React.ComponentType<{ className?: string; fill?: string; stroke?: string }>;
    iconClassName: string;
    fill?: string;
    stroke?: string;
  }
> = {
  success: {
    IconComponent: Check,
    iconClassName: successIconClass,
  },
  fail: {
    IconComponent: X,
    iconClassName: failIconClass,
  },
  warning: {
    IconComponent: TriangleAlert,
    iconClassName: warningIconClass,
  },
  information: {
    IconComponent: InformationIcon,
    iconClassName: informationIconClass,
  },
  highlight: {
    IconComponent: Star,
    iconClassName: highlightIconClass,
    fill: "currentColor",
    stroke: "none",
  },
  locked: {
    IconComponent: Lock,
    iconClassName: lockedIconClass,
  },
  white: {
    iconClassName: whiteIconClass,
  },
  dark: {
    iconClassName: darkIconClass,
  },
  skipped: {
    IconComponent: X,
    iconClassName: skippedIconClass,
  },
};

export const Hexagon: React.FC<HexagonProps> = ({ type, size, character }) => {
  const { IconComponent, iconClassName, fill, stroke } = VARIANT_CONFIG[type];
  const showCharacter =
    character && (type === "information" || type === "white" || type === "dark");

  return (
    <svg
      aria-hidden="true"
      className={`${hexagonRoot} ${iconClassName}`}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={size ? { width: size, height: size } : undefined}
    >
      <path
        d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z"
        className={hexagonShape}
      />
      {showCharacter ? (
        <text x="14" y="16" className={charLabelClass}>
          {character}
        </text>
      ) : (
        IconComponent && (
          <foreignObject x="6.5" y="8.5" width="15" height="15">
            <div className={iconContainer}>
              <IconComponent
                className={iconClass}
                {...(fill && { fill })}
                {...(stroke && { stroke })}
              />
            </div>
          </foreignObject>
        )
      )}
    </svg>
  );
};
